import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Business,
  Assessment,
  TrendingUp,
  Warning,
  CheckCircle,
  Schedule,
  Assignment,
  Analytics,
  ShowChart,
} from '@mui/icons-material';
import { Company, companyAPI, analyticsAPI } from '../services/api';
import { useCompany } from '../contexts/CompanyContext';
import { useConsultantBranding } from '../contexts/ConsultantBrandingContext';
import axios from 'axios';

const CompanyDashboard: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const { companies } = useCompany();
  const { setBrandingFromDashboard } = useConsultantBranding();
  const [company, setCompany] = useState<Company | null>(null);
  const [companyAnalytics, setCompanyAnalytics] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    const loadCompanyData = async () => {
      if (!companyId) return;
      
      try {
        setIsLoading(true);
        setAccessDenied(false);
        
        // OPTIMIZED: Load company, analytics, and branding in one API call
        const dashboardData = await companyAPI.getCompanyDashboard(parseInt(companyId));
        
        setCompany(dashboardData.company);
        setCompanyAnalytics(dashboardData.analytics);
        
        // Set branding data from combined response to avoid separate API call
        if (dashboardData.branding) {
          setBrandingFromDashboard(dashboardData.branding);
        }
        
        console.log('✅ Company dashboard data loaded (optimized single request):', dashboardData.company.name);
        
      } catch (error) {
        console.error('Failed to load company data:', error);
        
        // Check if this is an access denied error (403) or not found (404)
        if (axios.isAxiosError(error) && (error.response?.status === 403 || error.response?.status === 404)) {
          console.log('🚫 Access denied to company', companyId, '- redirecting to default company');
          setAccessDenied(true);
          
          // Find user's default company and redirect
          if (companies.length > 0) {
            const defaultCompany = companies[0];
            console.log('↩️ Redirecting to:', defaultCompany.name);
            navigate(`/company/${defaultCompany.id}`, { replace: true });
            return;
          } else {
            // No companies available - redirect to dashboard
            navigate('/dashboard', { replace: true });
            return;
          }
        }
        
        // For other errors, show error state
        setCompany(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadCompanyData();
  }, [companyId, companies, navigate]);

  const handleManageAudits = () => {
    navigate(`/company/${companyId}/audits`);
  };

  const handleViewAnalysis = () => {
    navigate(`/audit-results?company=${companyId}&view=analysis`);
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (accessDenied) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="warning">
          You don't have access to this company. Redirecting to your default company...
        </Alert>
        <CircularProgress sx={{ mt: 2 }} />
      </Container>
    );
  }

  if (!company) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">Company not found or you don't have access to it.</Alert>
        <Button 
          variant="contained" 
          sx={{ mt: 2 }} 
          onClick={() => navigate('/dashboard')}
        >
          Go to Dashboard
        </Button>
      </Container>
    );
  }

  // Use real analytics data - health_score can be null if no audits
  const healthScore = companyAnalytics?.health_score;
  const hasHealthScore = healthScore !== null && healthScore !== undefined;
  const overdueAudits = companyAnalytics?.overdue_audits || 0;
  const inProgressAudits = companyAnalytics?.in_progress_audits || 0;
  const openTasks = companyAnalytics?.open_tasks || 0;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
        <Avatar sx={{ mr: 2, bgcolor: 'primary.main', width: 56, height: 56 }}>
          <Business fontSize="large" />
        </Avatar>
        <Box>
          <Typography variant="h4" component="h1">
            {company.name}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {company.city} • {company.employee_count} employees • {company.sites_count ?? company.site_count ?? 0} sites
          </Typography>
        </Box>
      </Box>

      {/* Key Metrics */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 3, mb: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color={hasHealthScore ? 'primary.main' : 'text.disabled'}>
                {hasHealthScore ? `${healthScore}%` : 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Health Score
              </Typography>
              <Box sx={{ mt: 1 }}>
                {hasHealthScore ? (
                  <Chip 
                    label={healthScore >= 70 ? "Good" : healthScore >= 50 ? "Fair" : "Needs Attention"} 
                    color={healthScore >= 70 ? "success" : healthScore >= 50 ? "warning" : "error"} 
                    size="small"
                    icon={<TrendingUp />}
                  />
                ) : (
                  <Chip 
                    label="No review data" 
                    color="default" 
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color={overdueAudits > 0 ? 'error.main' : 'success.main'}>
                {overdueAudits}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Overdue Reviews
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Chip 
                  label={overdueAudits > 0 ? "Action Required" : "Up to Date"} 
                  color={overdueAudits > 0 ? "error" : "success"} 
                  size="small"
                />
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color={inProgressAudits > 0 ? 'info.main' : 'text.disabled'}>
                {inProgressAudits}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                In Progress
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Chip 
                  label={inProgressAudits > 0 ? "Active" : "None"} 
                  color={inProgressAudits > 0 ? "info" : "default"} 
                  size="small"
                  icon={inProgressAudits > 0 ? <Schedule /> : undefined}
                  variant={inProgressAudits > 0 ? "filled" : "outlined"}
                />
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color={openTasks > 0 ? 'warning.main' : 'success.main'}>
                {openTasks}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Open Tasks
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Chip 
                  label={openTasks > 0 ? "Follow Up" : "Complete"} 
                  color={openTasks > 0 ? "warning" : "success"} 
                  size="small"
                />
              </Box>
            </CardContent>
          </Card>
        </Box>

      {/* Main Content */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {/* Recent Activity */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Activity
              </Typography>
              {companyAnalytics?.recent_activity && companyAnalytics.recent_activity.length > 0 ? (
                <List>
                  {companyAnalytics.recent_activity.map((activity: any, index: number) => (
                    <React.Fragment key={index}>
                      <ListItem>
                        <ListItemIcon>
                          {activity.icon === 'check_circle' && <CheckCircle color="success" />}
                          {activity.icon === 'schedule' && <Schedule color="info" />}
                          {activity.icon === 'warning' && <Warning color="warning" />}
                        </ListItemIcon>
                        <ListItemText
                          primary={activity.title}
                          secondary={activity.description}
                        />
                      </ListItem>
                      {index < companyAnalytics.recent_activity.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Alert severity="info">
                  No recent activity found. Start a review to see activity here!
                </Alert>
              )}
            </CardContent>
          </Card>

        {/* Quick Actions */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<Assessment />}
                onClick={handleManageAudits}
                data-testid="manage-audits-button"
              >
                Manage Reviews
              </Button>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<Assignment />}
                onClick={() => navigate(`/audit-results?company=${companyId}&filter=action-items`)}
                data-testid="manage-action-items-button"
              >
                Manage Action Items
              </Button>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<ShowChart />}
                onClick={() => navigate(`/company/${companyId}/kpis`)}
                data-testid="track-kpis-button"
              >
                Track KPIs
              </Button>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<Analytics />}
                onClick={handleViewAnalysis}
                data-testid="view-analysis-button"
              >
                View Analysis & Reports
              </Button>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<Business />}
                onClick={() => navigate(`/company/${companyId}/settings`)}
                data-testid="company-settings-button"
              >
                Company Settings
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default CompanyDashboard; 